import { apiPath } from ".";
import { ColumnDescription } from "../Model/Column";
import Entry from "../Model/Entry";

export interface GetResponse {
  rowCount: number;
  results: Entry[];
}
export interface GetOneResponse {
  status: "success" | "not-found" | "server-error";
}

export interface GetOneResponseSuccess extends GetOneResponse {
  status: "success";
  entry: Entry;
}

export interface CreateResponse {
  success: boolean;
  status: number;
  errorBody?: string;
}

export type IUpdateResponse = CreateResponse;

/**
 * Wrapper for all the REST API functions related to one table
 */
export class TableAPI {
  /** Resource path of the api: $apiPath/$resourcePath/... */
  private resourcePath: string;

  constructor(path: string) {
    this.resourcePath = path;
  }

  /**
   * Get the columns of this table
   */
  public async getColumns(): Promise<Record<string, ColumnDescription>> {
    const response = await fetch(`${apiPath}/${this.resourcePath}/columns`);
    return await response.json();
  }

  /**
   * Gets `T` from server
   * @param count       How many to get
   * @param offset      Offset to start page
   * @param orderBy     By which column to order
   * @param queryParams Query params passed raw to the GET path (start with &)
   */
  public async get(
    count = 10,
    offset = 0,
    orderBy = "",
    queryParams: string
  ): Promise<GetResponse | undefined> {
    // Make the request
    let response: Response;
    try {
      response = await fetch(
        `${apiPath}/${this.resourcePath}/?count=${count}&offset=${offset}&orderBy=${orderBy}${queryParams}`
      );
    } catch (ex) {
      return undefined;
    }

    // If status code is not 200 return server-error
    if (!response.ok) {
      return undefined;
    }

    // Return results
    const json = (await response.json()) as GetResponse;
    return { ...json };
  }

  public async getOne(
    id: number
  ): Promise<GetOneResponse | GetOneResponseSuccess> {
    // Make the request
    let response: Response;
    try {
      response = await fetch(`${apiPath}/${this.resourcePath}/${id}`);
    } catch (ex) {
      return { status: "server-error" };
    }

    // If status code is not 200, return server-error
    if (!response.ok) {
      return { status: "server-error" };
    }

    // Check if found
    if (response.status === 404) {
      return { status: "not-found" };
    }

    // Result results
    const json = (await response.json()) as Entry;
    return { status: "success", entry: json };
  }

  /**
   * Create a new row
   * @param body        Row to create, sent as POST request body, not validated client-side
   */
  public async create(body: Entry): Promise<CreateResponse> {
    const request: RequestInit = {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
      redirect: "follow",
      body: JSON.stringify(body),
    };

    const response = await fetch(`${apiPath}/${this.resourcePath}/`, request);
    if (response.ok) {
      return { success: true, status: response.status };
    } else {
      return {
        success: false,
        status: response.status,
        errorBody: await response.text(),
      };
    }
  }

  /**
   * Patch a new row
   * @param entryId     Which row to patch (id)
   * @param body        Row to create, sent as PUT request body, not validated client-side
   */
  public async update(entryId: number, body: Entry): Promise<IUpdateResponse> {
    const request: RequestInit = {
      method: "PUT",
      mode: "cors",
      cache: "no-cache",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
      redirect: "follow",
      body: JSON.stringify(body),
    };

    const response = await fetch(
      `${apiPath}/${this.resourcePath}/row/${entryId}`,
      request
    );
    if (response.ok) {
      return { success: true, status: response.status };
    } else {
      return {
        success: false,
        status: response.status,
        errorBody: await response.text(),
      };
    }
  }

  /**
   * Request to delete a row.
   * Only admin users have permission to do this server-side.
   *
   * @param {number} entryId      Which row to delete
   * @returns {Promise<boolean>}  True for success, false otherwise
   */
  public async delete(entryId: number): Promise<boolean> {
    const request: RequestInit = {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      headers: {
        Accept: "application/json",
      },
      redirect: "follow",
    };

    const response = await fetch(
      `${apiPath}/${this.resourcePath}/row/${entryId}`,
      request
    );
    return response.ok;
  }
}
